import { Eye, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { SkeletonList } from '../ui/Skeleton';
import memberAvatarPlaceholder from '../../assets/images/member-avatar-placeholder.png';
import { ReactComponent as MemberInsuranceOkIcon } from '../../assets/icons/member-insurance-ok.svg';

function subscriptionLabel(status, t) {
    if (status === 'active') return t('gym.membersManager.memberStatus.activeSubscription');
    if (status === 'expired') return t('gym.membersManager.memberStatus.expiredSubscription');
    return t('gym.membersManager.memberStatus.noSubscription');
}

function subscriptionBadgeClass(status) {
    if (status === 'active') return 'bg-blue-100 text-blue-800 dark:bg-teal-800 dark:text-white';
    if (status === 'expired') return 'bg-orange-100 text-orange-800 dark:bg-orange-950/50 dark:text-orange-200';
    return 'bg-gray-100 text-gray-700 dark:bg-white/15 dark:text-white/90';
}

export default function MembersTableView({
    members,
    listLoading,
    selectedMemberId,
    onSelectMember,
    statusView,
    isGym,
    isTrainer,
    totalMembers,
    membersPage,
    pageSize,
    onPageChange,
    emptyTitle,
    emptyDescription,
}) {
    const { t } = useTranslation();
    const totalPages = Math.max(1, Math.ceil(totalMembers / pageSize));

    return (
        <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-gray-800">
            {listLoading ? (
                <div className="p-4">
                    <SkeletonList rows={8} />
                </div>
            ) : members.length === 0 ? (
                <div className="flex flex-1 flex-col items-center justify-center px-6 text-center text-gray-500 dark:text-white/70">
                    <Users size={48} className="mb-3 opacity-40" />
                    <p className="font-semibold">{emptyTitle}</p>
                    {emptyDescription ? (
                        <p className="mt-2 text-sm text-gray-400 dark:text-white/55">{emptyDescription}</p>
                    ) : null}
                </div>
            ) : (
                <>
                    <div className="flex-1 overflow-auto">
                        <table className="min-w-full text-left text-sm">
                            <thead className="sticky top-0 z-10 border-b border-gray-200 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:border-white/10 dark:bg-gray-900 dark:text-white/60">
                                <tr>
                                    <th className="px-4 py-3">{t('gym.membersManager.table.client')}</th>
                                    <th className="hidden px-4 py-3 md:table-cell">{t('gym.membersManager.table.email')}</th>
                                    <th className="hidden px-4 py-3 lg:table-cell">{t('gym.membersManager.filters.subscriptionLabel')}</th>
                                    <th className="hidden px-4 py-3 xl:table-cell">{t('gym.membersManager.table.joinedAt')}</th>
                                    <th className="px-4 py-3 text-right">{t('gym.membersManager.table.actions')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-white/5">
                                {members.map((member) => {
                                    const profilePhotoUrl = member?.avatar_url || member?.profile_image_url || member?.photo_url || null;
                                    const isSelected = selectedMemberId === member.id;

                                    return (
                                        <tr
                                            key={member.id}
                                            data-member-id={member.id}
                                            onClick={() => onSelectMember(member)}
                                            className={`cursor-pointer transition-colors ${
                                                isSelected
                                                    ? 'bg-indigo-50 dark:bg-indigo-950/40'
                                                    : 'hover:bg-indigo-50/70 dark:hover:bg-indigo-950/25'
                                            }`}
                                        >
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full bg-blue-600">
                                                        <img
                                                            src={profilePhotoUrl || memberAvatarPlaceholder}
                                                            alt=""
                                                            className="h-full w-full object-cover"
                                                            loading="lazy"
                                                        />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="truncate font-medium text-gray-900 dark:text-white">
                                                            {member.name}
                                                        </p>
                                                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                                            {member.role_in_gym === 'trainer' && statusView === 'trainer' && (
                                                                <span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-800 dark:bg-white/15 dark:text-white">
                                                                    {t('gym.membersManager.memberCard.trainer')}
                                                                </span>
                                                            )}
                                                            {member.role_in_gym === 'member' && member.insuranceStatus === 'ok' && (
                                                                <span title={t('gym.membersManager.insuranceBadge.ok')}>
                                                                    <MemberInsuranceOkIcon className="h-4 w-4 text-[#00cc99]" aria-hidden />
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="hidden px-4 py-3 text-gray-600 dark:text-white/75 md:table-cell">
                                                <span className="block max-w-[220px] truncate">{member.email}</span>
                                            </td>
                                            <td className="hidden px-4 py-3 lg:table-cell">
                                                {member.role_in_gym === 'member' && member.subscriptionStatus && (
                                                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${subscriptionBadgeClass(member.subscriptionStatus)}`}>
                                                        {subscriptionLabel(member.subscriptionStatus, t)}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="hidden px-4 py-3 text-gray-600 dark:text-white/75 xl:table-cell">
                                                {member.joined_at
                                                    ? new Date(member.joined_at).toLocaleDateString('it-IT')
                                                    : '-'}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center justify-end gap-1">
                                                    {member.role_in_gym === 'member' && (
                                                        <Link
                                                            to={`/gym/statistics-individual?memberId=${member.id}`}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="hidden rounded-lg px-2 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-100 dark:text-indigo-300 dark:hover:bg-indigo-950/50 sm:inline-flex"
                                                        >
                                                            {t('gym.membersManager.memberDetail.viewStatistics')}
                                                        </Link>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onSelectMember(member);
                                                        }}
                                                        className="inline-flex rounded-lg p-2 text-indigo-600 hover:bg-indigo-100 dark:text-indigo-300 dark:hover:bg-indigo-950/50"
                                                        aria-label={t('gym.membersManager.table.openProfile')}
                                                    >
                                                        <Eye size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {(isGym || isTrainer) && totalMembers > pageSize && (
                        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-4 py-3 dark:border-white/10">
                            <span className="text-sm text-gray-600 dark:text-white/60">
                                {t('gym.membersManager.history.pageOf', { current: membersPage + 1, total: totalPages })}
                                {' · '}
                                {totalMembers} {t('gym.membersManager.statusTabs.clients').toLowerCase()}
                            </span>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    disabled={membersPage === 0}
                                    onClick={() => onPageChange(membersPage - 1)}
                                    className="min-h-[40px] rounded-lg border px-3 py-1.5 text-sm font-medium disabled:opacity-40 dark:border-gray-600"
                                >
                                    {t('gym.membersManager.history.prev')}
                                </button>
                                <button
                                    type="button"
                                    disabled={membersPage >= totalPages - 1}
                                    onClick={() => onPageChange(membersPage + 1)}
                                    className="min-h-[40px] rounded-lg border px-3 py-1.5 text-sm font-medium disabled:opacity-40 dark:border-gray-600"
                                >
                                    {t('gym.membersManager.history.next')}
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
